/* Widget styles — scoped via shadow DOM; no Tailwind.
   Satoshi is loaded by a sibling <link> + <style>@import in the shadow
   root (see injectSatoshiFontInto in index.ts) so the font-face is
   registered before this stylesheet's rules apply. */
:host {
  all: initial;
  font-family:
    "Satoshi",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Helvetica,
    Arial,
    sans-serif;
}

.hk-root {
  position: fixed;
  z-index: 2147483600;
  bottom: 20px;
  font-size: 14px;
  color: #111827;
  /* Set the brand font here (not only on :host): the host element carries an
     inline `all: initial`, which outranks the :host stylesheet rule and would
     otherwise reset font-family to the UA default. .hk-root is shadow-internal,
     so this wins and cascades to the whole widget (launcher + dialog). */
  font-family:
    "Satoshi",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Helvetica,
    Arial,
    sans-serif;
}

/* Form controls don't inherit font-family from their parent by default (the UA
   stylesheet gives buttons/inputs/textarea their own font). Force them to
   inherit so the launcher, close button, and call controls all use Satoshi. */
.hk-root button,
.hk-root input,
.hk-root textarea,
.hk-root select {
  font-family: inherit;
}

/* Typed-answer composer — a single rounded pill containing the input and a
   circular icon-only send button (modern chat-composer pattern). */
.hk-input-row {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 4px 4px 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}
.hk-input-row:focus-within {
  border-color: var(--hk-color, #111827);
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.08);
  box-shadow: 0 0 0 3px
    color-mix(in srgb, var(--hk-color, #111827) 16%, transparent);
}
.hk-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 28px;
  border: 0;
  background: transparent;
  font-size: 14px;
  color: #111827;
  outline: 0;
  text-overflow: ellipsis;
}
.hk-input::placeholder {
  color: #6b7280;
}
.hk-input-send {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  transition:
    filter 0.15s ease,
    transform 0.12s ease;
}
.hk-input-send svg {
  width: 15px;
  height: 15px;
}
.hk-input-send:hover {
  filter: brightness(0.92);
}
.hk-input-send:active {
  transform: scale(0.92);
}

/* ─── Conversation transcript (chat) ───────────────────────────────────── */
.hk-transcript {
  width: 100%;
  height: clamp(150px, 30vh, 240px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 2px;
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
}
.hk-transcript::-webkit-scrollbar {
  width: 6px;
}
.hk-transcript::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 3px;
}
.hk-transcript::-webkit-scrollbar-track {
  background: transparent;
}
.hk-transcript-empty {
  margin: auto;
  max-width: 220px;
  text-align: center;
  font-size: 12px;
  color: #9ca3af;
}
.hk-msg {
  display: flex;
  width: 100%;
}
.hk-msg.agent {
  justify-content: flex-start;
}
.hk-msg.user {
  justify-content: flex-end;
}
.hk-bubble {
  max-width: 82%;
  padding: 8px 12px;
  border-radius: 14px;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  animation: hk-msg-in 0.18s ease-out;
}
.hk-msg.agent .hk-bubble {
  background: #f3f4f6;
  color: #111827;
  border-bottom-left-radius: 5px;
}
.hk-msg.user .hk-bubble {
  color: #ffffff;
  border-bottom-right-radius: 5px;
  /* background set inline to the brand color */
}
@keyframes hk-msg-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.hk-root.position-bottom-right {
  right: 20px;
}
.hk-root.position-bottom-left {
  left: 20px;
}

/* Greeting teaser — a chat bubble that floats above the closed launcher. */
.hk-teaser {
  position: absolute;
  bottom: 64px;
  min-width: 230px;
  max-width: 300px;
  width: max-content;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 14px;
  background: #ffffff;
  color: #111827;
  font-size: 13px;
  line-height: 1.45;
  cursor: pointer;
  box-shadow:
    0 10px 25px rgba(0, 0, 0, 0.15),
    0 4px 6px rgba(0, 0, 0, 0.08);
  animation: hk-msg-in 0.22s ease-out;
}
.hk-root.position-bottom-right .hk-teaser {
  right: 0;
  border-bottom-right-radius: 5px;
}
.hk-root.position-bottom-left .hk-teaser {
  left: 0;
  border-bottom-left-radius: 5px;
}
.hk-teaser-text {
  flex: 1 1 auto;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.hk-teaser-close {
  flex: none;
  border: 0;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0 2px;
}
.hk-teaser-close:hover {
  color: #4b5563;
}

.hk-launcher {
  position: relative;
  border: 0;
  outline: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border-radius: 999px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.01em;
  box-shadow:
    0 10px 25px rgba(0, 0, 0, 0.15),
    0 4px 6px rgba(0, 0, 0, 0.08);
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;
}
.hk-launcher:hover {
  transform: translateY(-1px);
}
.hk-launcher:active {
  transform: translateY(0);
}
.hk-launcher svg {
  width: 16px;
  height: 16px;
  transform-origin: 50% 50%;
}

/* Attention loop — runs only when dialog is closed. A soft outward ripple
   on the button plus a brief phone-icon wiggle, on a 4s cycle so it
   breathes rather than buzzes. */
.hk-launcher.attention::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  box-shadow: 0 0 0 0 currentColor;
  opacity: 0.55;
  pointer-events: none;
  animation: hk-launcher-pulse 4s ease-out infinite;
}
.hk-launcher.attention svg {
  animation: hk-icon-wiggle 4s ease-in-out infinite;
}

@keyframes hk-launcher-pulse {
  0% {
    box-shadow: 0 0 0 0 currentColor;
    opacity: 0.45;
  }
  70% {
    box-shadow: 0 0 0 14px currentColor;
    opacity: 0;
  }
  100% {
    box-shadow: 0 0 0 14px currentColor;
    opacity: 0;
  }
}

@keyframes hk-icon-wiggle {
  0%,
  72%,
  100% {
    transform: rotate(0deg);
  }
  76% {
    transform: rotate(-12deg);
  }
  80% {
    transform: rotate(10deg);
  }
  84% {
    transform: rotate(-8deg);
  }
  88% {
    transform: rotate(6deg);
  }
  92% {
    transform: rotate(0deg);
  }
}

.hk-panel {
  position: absolute;
  bottom: 64px;
  width: 344px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow:
    0 20px 50px rgba(0, 0, 0, 0.18),
    0 6px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  animation: hk-panel-in 0.24s cubic-bezier(0.16, 1, 0.3, 1);
}
.hk-root.position-bottom-right .hk-panel {
  right: 0;
  transform-origin: bottom right;
}
.hk-root.position-bottom-left .hk-panel {
  left: 0;
  transform-origin: bottom left;
}
@keyframes hk-panel-in {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.hk-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  color: #ffffff;
}
.hk-header .hk-title {
  font-size: 14px;
  font-weight: 600;
}
.hk-close {
  background: transparent;
  border: 0;
  color: #ffffff;
  cursor: pointer;
  opacity: 0.85;
  padding: 0;
  font-size: 18px;
  line-height: 1;
}
.hk-close:hover {
  opacity: 1;
}

.hk-body {
  padding: 18px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  min-height: 180px;
}
.hk-status {
  font-size: 13px;
  color: #4b5563;
}
.hk-status.error {
  color: #b91c1c;
}
.hk-spinner {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #e5e7eb;
  border-top-color: #6b7280;
  animation: hk-spin 0.8s linear infinite;
}
@keyframes hk-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ─── Voice-assistant stage ────────────────────────────────────────────── */

.hk-stage {
  position: relative;
  width: 104px;
  height: 104px;
  display: flex;
  align-items: center;
  justify-content: center;
}
/* Soft brand-tinted halo behind the orb for depth. */
.hk-stage::before {
  content: "";
  position: absolute;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: rgba(17, 24, 39, 0.06);
  background: radial-gradient(
    circle,
    color-mix(in srgb, var(--hk-color, #111827) 18%, transparent),
    transparent 68%
  );
  z-index: 0;
  pointer-events: none;
}
.hk-orb {
  position: relative;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: var(--hk-color, #111827);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  z-index: 2;
  transition:
    transform 220ms ease-out,
    opacity 220ms ease-out;
}
.hk-ring {
  position: absolute;
  inset: 0;
  margin: auto;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: 2px solid var(--hk-color, #111827);
  opacity: 0;
  z-index: 1;
  pointer-events: none;
}

/* listening — gentle breath + slow outward ring */
.hk-stage.listening .hk-orb {
  animation: hk-breathe 2.8s ease-in-out infinite;
}
.hk-stage.listening .hk-ring:nth-child(1) {
  animation: hk-emit 2.8s ease-out infinite;
}
.hk-stage.listening .hk-ring:nth-child(2),
.hk-stage.listening .hk-ring:nth-child(3) {
  display: none;
}

/* speaking — faster pulse + three staggered emanating rings */
.hk-stage.speaking .hk-orb {
  animation: hk-orb-pulse 0.95s ease-out infinite;
}
.hk-stage.speaking .hk-ring:nth-child(1) {
  animation: hk-emit-fast 1.6s ease-out infinite;
}
.hk-stage.speaking .hk-ring:nth-child(2) {
  animation: hk-emit-fast 1.6s ease-out 0.4s infinite;
}
.hk-stage.speaking .hk-ring:nth-child(3) {
  animation: hk-emit-fast 1.6s ease-out 0.8s infinite;
}

/* thinking — orb dims, three dots overlay (processing affordance) */
.hk-stage.thinking .hk-orb {
  opacity: 0.45;
  transform: scale(0.9);
}
.hk-stage.thinking .hk-ring {
  display: none;
}
.hk-think-dots {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  z-index: 3;
  pointer-events: none;
}
.hk-think-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ffffff;
  opacity: 0.25;
  animation: hk-think-dot 1.2s ease-in-out infinite;
}
.hk-think-dots span:nth-child(2) {
  animation-delay: 0.18s;
}
.hk-think-dots span:nth-child(3) {
  animation-delay: 0.36s;
}

/* idle / connecting / failed — static orb (rings off) */
.hk-stage.idle .hk-ring,
.hk-stage.connecting .hk-ring,
.hk-stage.failed .hk-ring,
.hk-stage.ended .hk-ring {
  display: none;
}
.hk-stage.connecting .hk-orb {
  opacity: 0.65;
}
.hk-stage.ended .hk-orb {
  opacity: 0.5;
  transform: scale(0.95);
}

@keyframes hk-breathe {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.92;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
}
@keyframes hk-orb-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}
@keyframes hk-emit {
  0% {
    transform: scale(0.85);
    opacity: 0.4;
  }
  100% {
    transform: scale(1.85);
    opacity: 0;
  }
}
@keyframes hk-emit-fast {
  0% {
    transform: scale(0.9);
    opacity: 0.55;
  }
  100% {
    transform: scale(2.25);
    opacity: 0;
  }
}
@keyframes hk-think-dot {
  0%,
  60%,
  100% {
    opacity: 0.25;
    transform: translateY(0) scale(0.85);
  }
  30% {
    opacity: 1;
    transform: translateY(-2px) scale(1.05);
  }
}

/* Call-ended badge */
.hk-ended-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #4b5563;
}
.hk-ended-badge svg {
  width: 14px;
  height: 14px;
}

/* Respect user's motion preference */
@media (prefers-reduced-motion: reduce) {
  .hk-orb,
  .hk-ring,
  .hk-think-dots span,
  .hk-spinner,
  .hk-panel,
  .hk-bubble,
  .hk-teaser,
  .hk-launcher.attention::after,
  .hk-launcher.attention svg {
    animation: none !important;
  }
  .hk-launcher.attention::after {
    display: none;
  }
  .hk-orb {
    opacity: 1;
    transform: none;
  }
  .hk-ring {
    display: none;
  }
}

/* Mic-permission gate inside the dialog body. */
.hk-permission {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
}
.hk-permission-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #374151;
}
.hk-permission-icon svg {
  width: 22px;
  height: 22px;
}
.hk-permission-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}
.hk-permission-body {
  font-size: 13px;
  color: #4b5563;
  line-height: 1.45;
  max-width: 240px;
}
.hk-permission .hk-control-primary {
  margin-top: 4px;
  border: 0;
  border-radius: 999px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: var(--hk-color, #111827);
  cursor: pointer;
  transition: filter 0.15s ease;
}
.hk-permission .hk-control-primary:hover {
  filter: brightness(0.92);
}
.hk-permission .hk-control-primary:disabled {
  opacity: 0.6;
  cursor: default;
}

/* Bottom action bar: composer grows, icon-only controls on the right. When the
   text input is disabled there's no composer, so the controls center instead. */
.hk-actionbar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 2px;
}
.hk-icon-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #111827;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    transform 0.12s ease;
}
.hk-icon-btn svg {
  width: 18px;
  height: 18px;
}
.hk-icon-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}
.hk-icon-btn:active {
  transform: scale(0.94);
}
.hk-icon-btn[disabled] {
  opacity: 0.45;
  cursor: default;
}
/* Muted state — mic-off shown in a soft red. */
.hk-icon-btn.active {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
}
/* End call — solid red, handset rotated to the classic "hang up" angle. */
.hk-icon-btn.danger {
  background: #dc2626;
  border-color: #dc2626;
  color: #ffffff;
}
.hk-icon-btn.danger:hover {
  background: #b91c1c;
  border-color: #b91c1c;
}
.hk-icon-btn.danger svg {
  transform: rotate(135deg);
}

/* Text control (used by the error view's Close button). */
.hk-control {
  border: 1px solid #e5e7eb;
  background: #ffffff;
  border-radius: 999px;
  padding: 9px 18px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    transform 0.12s ease;
}
.hk-control:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

/* Keyboard focus rings — visible affordance restored for all interactive
   controls (they otherwise set outline:0). Pointer users don't see these. */
.hk-launcher:focus-visible,
.hk-control:focus-visible,
.hk-icon-btn:focus-visible,
.hk-input-send:focus-visible,
.hk-permission .hk-control-primary:focus-visible {
  outline: 2px solid var(--hk-color, #111827);
  outline-offset: 2px;
}
.hk-close:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.9);
  outline-offset: 2px;
  border-radius: 4px;
}

.hk-footer {
  padding: 10px 16px;
  font-size: 11px;
  color: #9ca3af;
  text-align: center;
  border-top: 1px solid #f3f4f6;
}
.hk-footer-link {
  color: #4b5563;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.15s ease;
}
.hk-footer-link:hover {
  color: #111827;
  text-decoration: underline;
}
